import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { translate, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { DepositTable, TablesPropertiesDto } from '@connect-services/client/sdk/api';
import {
    ColumnType,
    CreateDepositLayerService,
} from '@connect-services/client/services/deposit/layer/create-deposit-layer.service';
import { TranslateService } from '@connect-services/client/services/translate.service';
import { ValidatorsService } from '@connect-services/client/services/validators.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { InputNumberComponent } from '@connect-services/src/lib/components/input/input-number/input-number.component';
import { InputTextComponent } from '@connect-services/src/lib/components/input/text/input-text/input-text.component';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { CustomColumn, CustomTableComponent } from '@connect-services/src/lib/components/table/table.component';
import { FormErrorEnum } from '@shared/client/models/form.model';
import { CommonValidatorsService } from '@shared/client/services/common-validators.service';
import { FormService } from '@shared/client/services/form.service';

export type ColumnForm = FormGroup<{
    length: FormControl<number | null>;
    name: FormControl<string>;
    required: FormControl<boolean>;
    type: FormControl<ColumnType | null>;
}>;

export type GeometryType = 'LineString' | 'Point' | 'Polygon';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        CheckboxComponent,
        ComplexModalTemplate,
        CustomTableComponent,
        FaIconComponent,
        InputNumberComponent,
        InputTextComponent,
        ReactiveFormsModule,
        SelectComponent,
        TranslocoPipe,
    ],
    selector: 'app-create-deposit-layer-modal',
    styleUrl: './create-deposit-layer-modal.component.scss',
    templateUrl: './create-deposit-layer-modal.component.html',
})
export class CreateDepositLayerModalComponent {
    public layerForm!: FormGroup<{
        columnProperties: FormArray<ColumnForm>;
        geometryType: FormControl<GeometryType | null>;
        name: FormControl<string>;
    }>;

    protected readonly ATTRIBUTE_LIMIT: number = 50;
    protected readonly attributeFormatByType: Partial<Record<ColumnType, string>>;
    protected readonly ButtonType = ButtonType;
    protected readonly CheckboxState = CheckboxState;
    protected readonly ColumnType = ColumnType;
    protected readonly customColumns: CustomColumn[] = [
        {
            key: 'name',
            label: 'fieldName',
            translate: true,
        },
        {
            key: 'type',
            label: 'Type',
            translate: false,
        },
        {
            key: 'property',
            label: 'property',
            translate: true,
        },
        {
            key: 'required',
            label: 'required',
            translate: true,
        },
    ];
    protected dialogRef = inject(MatDialogRef<CreateDepositLayerModalComponent>);
    protected readonly geometryTypeDefaultValue = signal<SelectOption<GeometryType>[]>([]);
    protected readonly geometryTypeOptions: SelectOption<GeometryType>[] = [
        { label: 'Points', value: 'Point' },
        { label: translate('line'), value: 'LineString' },
        { label: translate('polygon'), value: 'Polygon' },
    ];
    protected readonly hasGeomType = signal<boolean>(false);
    protected typeDefaultOption: SelectOption<ColumnType>[];
    protected readonly typeOptions = computed<SelectOption<ColumnType>[]>(() => {
        return [
            {
                label: this.translocoService.translate('string'),
                value: ColumnType.STRING,
            },
            {
                label: 'Date',
                value: ColumnType.DATE,
            },
            {
                label: this.translocoService.translate('dateAndHour'),
                value: ColumnType.TIMESTAMPTZ,
            },
            {
                label: this.translocoService.translate('boolean'),
                value: ColumnType.BOOLEAN,
            },
            {
                disabledReason: this.hasGeomType()
                    ? this.translocoService.translate('onlyOneGeomColumnCanBeCreate')
                    : undefined,
                label: this.translocoService.translate('geom'),
                value: ColumnType.GEOM,
            },
            {
                label: 'Float',
                value: ColumnType.FLOAT,
            },
            {
                label: this.translocoService.translate('number'),
                value: ColumnType.NUMBER,
            },
        ];
    });

    private readonly createDepositLayerService = inject(CreateDepositLayerService);
    private readonly formService = inject(FormService);
    private readonly translateService = inject(TranslateService);
    private readonly translocoService = inject(TranslocoService);

    public constructor(private formBuilder: FormBuilder) {
        this.attributeFormatByType = {
            [ColumnType.DATE]: this.translateService.getDateFormatLabel(),
            [ColumnType.TIMESTAMPTZ]: this.translateService.getDateHourFormatLabel(),
            [ColumnType.BOOLEAN]: this.translocoService.translate('trueOrFalse'),
        };

        this.typeDefaultOption = [
            {
                label: this.translocoService.translate('string'),
                value: ColumnType.STRING,
            },
        ];

        this.layerForm = this.formBuilder.group({
            columnProperties: this.formBuilder.array<ColumnForm>([], {
                validators: [this.uniqueColumnNamesValidator()],
            }),
            geometryType: this.formBuilder.control<GeometryType | null>(null),
            name: this.formBuilder.nonNullable.control('', {
                validators: [
                    Validators.required,
                    Validators.minLength(FormService.VARCHAR_MIN_LENGTH),
                    Validators.maxLength(FormService.MAX_POSTGRESQL_IDENTIFIER_NAME),
                    CommonValidatorsService.forbiddenWords(Array.from(FormService.RESERVED_WORD)),
                    CommonValidatorsService.onlyAlphaNumeric(['_']),
                    this.tableNameNotExistsValidator([
                        ...this.createDepositLayerService.tables().map((table: DepositTable) => table.name ?? table),
                        ...this.createDepositLayerService
                            .newCreatedLayers()
                            .map((table: TablesPropertiesDto) => table.name ?? table),
                    ]),
                    ValidatorsService.cannotStartWithNumber(),
                ],
            }),
        });

        this.addRow();
    }

    protected get columnProperties(): FormArray<ColumnForm> {
        return this.layerForm.controls.columnProperties;
    }

    public getNameErrors(control: FormControl<string>, row?: ColumnForm): string[] {
        const errors: string[] = [];

        const controlErrors: ValidationErrors | null = control.errors;

        if (controlErrors) {
            errors.push(
                ...Object.entries(controlErrors)
                    .filter(([key]) => key !== FormErrorEnum.REQUIRED)
                    .map(([key, value]) =>
                        key === 'nameAlreadyUsed'
                            ? 'nameAlreadyUsed'
                            : this.formService.getErrorMessage({
                                  key,
                                  value,
                              }),
                    ),
            );
        }

        if (row?.errors?.['geomNameRequired']) {
            errors.push('geomNameRequired');
        }

        if (row?.errors?.['geomTypeRequired']) {
            errors.push('geomTypeRequired');
        }

        if (this.columnProperties.errors?.['duplicatedColumnNames']) {
            const currentName: string = control.value?.trim().toLowerCase();

            const duplicated: ColumnForm[] = this.columnProperties.controls.filter(
                (column: ColumnForm) => column.controls.name.value?.trim().toLowerCase() === currentName,
            );

            if (currentName && duplicated.length > 1) {
                errors.push('duplicatedColumnNames');
            }
        }

        return [...new Set(errors)];
    }

    protected addRow(): void {
        if (this.layerForm.invalid && this.layerForm.controls.columnProperties.length) {
            return;
        }

        this.columnProperties.push(this.createRow());
    }

    protected hasGeomColumn(): boolean {
        return this.columnProperties.controls.some(
            (column: ColumnForm) => column.controls.type.value === ColumnType.GEOM,
        );
    }

    protected isLengthInpuEnable(column: ColumnForm): boolean {
        return column.controls.type.value === ColumnType.STRING;
    }

    protected onClickEvent(modalResult: ModalResultEnum): void {
        if (modalResult === ModalResultEnum.CANCEL) {
            this.dialogRef.close(null);

            return;
        }
        this.dialogRef.close(this.layerForm.getRawValue());
    }

    protected removeRow(index: number): void {
        this.columnProperties.removeAt(index);
    }

    protected resetType(row: ColumnForm): void {
        row.controls.type.setValue(null);
    }

    protected selectedGeometryTypeChange(type: SelectOption<GeometryType>[]): void {
        if (!type.length) {
            this.layerForm.controls.geometryType.setValue(null);

            return;
        }

        this.layerForm.controls.geometryType.setValue(type[0].value);
    }

    protected selectedTypeChange(type: SelectOption<ColumnType>[], column: ColumnForm): void {
        if (!type.length) {
            column.controls.type.setValue(null);
            column.controls.length.reset();

            this.updateGeometryTypeValidity();

            return;
        }
        column.controls.type.setValue(type[0].value);
        column.controls.length.reset();

        if (type[0].value === ColumnType.GEOM) {
            column.controls.name.setValue('geom');
            column.controls.required.setValue(true);
        }

        this.updateGeometryTypeValidity();
    }

    private createRow(): ColumnForm {
        return this.formBuilder.group(
            {
                length: this.formBuilder.control<number | null>(null, {
                    validators: [Validators.min(1), Validators.max(254), CommonValidatorsService.integer],
                }),
                name: this.formBuilder.nonNullable.control('', {
                    validators: [
                        Validators.required,
                        Validators.minLength(FormService.VARCHAR_MIN_LENGTH),
                        Validators.maxLength(FormService.MAX_POSTGRESQL_IDENTIFIER_NAME),
                        CommonValidatorsService.forbiddenWords(Array.from(FormService.RESERVED_WORD)),
                        CommonValidatorsService.onlyAlphaNumeric(['_']),
                        ValidatorsService.cannotStartWithNumber(),
                    ],
                }),
                required: this.formBuilder.nonNullable.control<boolean>(false, {
                    validators: [Validators.required],
                }),
                type: this.formBuilder.control<ColumnType>(ColumnType.STRING, {
                    validators: [Validators.required],
                }),
            },
            { validators: [this.geometryColumnValidator()] },
        );
    }

    private geometryColumnValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const group = control as ColumnForm;

            const name: string = group.controls.name.value?.trim().toLowerCase();
            const type: ColumnType | null = group.controls.type.value;

            const isGeomType: boolean = type === ColumnType.GEOM;

            if (isGeomType && name !== 'geom') {
                return {
                    geomNameRequired: true,
                };
            }

            if (!isGeomType && name === 'geom') {
                return {
                    geomTypeRequired: true,
                };
            }

            return null;
        };
    }

    private tableNameNotExistsValidator(existingNames: string[]): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = (control.value ?? '').toString().trim().toLowerCase();

            if (!value) {
                return null;
            }

            const existing = existingNames.map((name: string) => name.trim().toLowerCase());

            return existing.includes(value) ? { ['nameAlreadyUsed']: { tableAlreadyExists: { value } } } : null;
        };
    }

    private uniqueColumnNamesValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const formArray = control as FormArray<ColumnForm>;

            const names: string[] = formArray.controls
                .map((column: ColumnForm): string => column.controls.name.value?.trim().toLowerCase())
                .filter(Boolean);

            const duplicated: boolean = names.some((name: string, index: number) => names.indexOf(name) !== index);

            return duplicated
                ? {
                      duplicatedColumnNames: true,
                  }
                : null;
        };
    }

    private updateGeometryTypeValidity(): void {
        const geometryTypeControl = this.layerForm.controls.geometryType;
        this.hasGeomType.set(this.hasGeomColumn());

        if (this.hasGeomColumn()) {
            geometryTypeControl.setValidators([Validators.required]);
            geometryTypeControl.updateValueAndValidity();

            return;
        }

        geometryTypeControl.clearValidators();
        geometryTypeControl.setValue(null);
        geometryTypeControl.updateValueAndValidity();

        this.geometryTypeDefaultValue.set([]);
    }
}
